/**
 * Permissions API
 * Handles permission management calls
 */
import apiClient from './apiClient';

export interface Permission {
  key: string;
  label: string;
  description: string;
}

export interface PermissionCategory {
  category: string;
  permissions: Permission[];
}

export interface AllPermissionsResponse {
  categories: PermissionCategory[];
  total: number;
}

export interface RoleDefaults {
  role: string;
  role_id: number | null;
  display_name: string;
  permissions: Record<string, boolean>;
}

export interface RoleDefaultsResponse {
  roles: RoleDefaults[];
}

export interface UserPermissionsResponse {
  user_id: number;
  user_name: string;
  roles: string[];
  role_permissions: Record<string, boolean>;
  overrides: Record<string, boolean>;
  effective_permissions: Record<string, boolean>;
}

export interface UpdatePermissionsData {
  overrides?: Record<string, boolean>;
  remove?: string[];
}

export const permissionsApi = {
  /**
   * Get all available permissions grouped by category
   */
  getAllPermissions: async (): Promise<AllPermissionsResponse> => {
    return apiClient.get<AllPermissionsResponse>('/permissions');
  },

  /**
   * Get default permissions for each role
   */
  getRoleDefaults: async (): Promise<RoleDefaultsResponse> => {
    return apiClient.get<RoleDefaultsResponse>('/permissions/roles/defaults');
  },

  /**
   * Get a user's effective permissions and overrides
   */
  getUserPermissions: async (userId: number): Promise<UserPermissionsResponse> => {
    return apiClient.get<UserPermissionsResponse>(`/permissions/user/${userId}`);
  },

  /**
   * Update a user's permission overrides (SuperAdmin only)
   */
  updateUserPermissions: async (userId: number, data: UpdatePermissionsData): Promise<{
    message: string;
    effective_permissions: Record<string, boolean>;
    overrides: Record<string, boolean>;
  }> => {
    return apiClient.put(`/permissions/user/${userId}`, data);
  },

  /**
   * Reset user to role defaults (remove all overrides)
   */
  resetUserPermissions: async (userId: number): Promise<{
    message: string;
    effective_permissions: Record<string, boolean>;
  }> => {
    return apiClient.post(`/permissions/user/${userId}/reset`);
  },
};

export default permissionsApi;
